import { signal } from '@preact/signals-react';
import { AppLanguage } from '../model/Types';
import i18n from 'i18next';
import {
    DEFAULT_LANG,
    SupportedLangCode,
    extractLangFromPath,
    langCodeToAppLanguage,
} from '../config/languages';

/**
 * Initial language detection priority:
 *   1. URL pathname (if it starts with a supported non-default lang)
 *   2. localStorage ('appLanguage' from previous sessions)
 *   3. Hard default (sr)
 *
 * Note: once the app is mounted, useLocaleSync keeps this signal
 * in sync with the URL on every navigation.
 */
const initCurrentLanguage = (): AppLanguage => {
    let code: SupportedLangCode = DEFAULT_LANG;

    if (typeof window !== 'undefined') {
        code = extractLangFromPath(window.location.pathname);

        // If URL has no lang prefix, fall back to stored preference
        if (code === DEFAULT_LANG) {
            const stored = localStorage.getItem('appLanguage');
            if (stored) {
                const normalized = stored.toLowerCase();
                if (
                    normalized === 'sr' ||
                    normalized === 'en' ||
                    normalized === 'de' ||
                    normalized === 'fr' ||
                    normalized === 'ru'
                ) {
                    code = normalized as SupportedLangCode;
                }
            }
        }
    }

    i18n.changeLanguage(code);
    return langCodeToAppLanguage(code);
};

export const appLanguage = signal<AppLanguage | null>(initCurrentLanguage());

/**
 * Programmatic language setter.
 *
 * Updates the signal, localStorage and i18n instance. Does NOT navigate —
 * URL navigation is the caller's responsibility (use useLocalizedNavigate or
 * the language switcher in the header).
 */
export const setLanguage = (language: AppLanguage) => {
    appLanguage.value = language;
    localStorage.setItem('appLanguage', language);
    i18n.changeLanguage(language.toLowerCase());
};
